import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { finalize, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ParkingPoint, ParkingPointSaveData } from '../../_types/parking-point.type';
import { ParkingsFilter } from '../../_types/parkings-filter.type';
import { GlobalLoaderService } from '../_core/global-loader.service';

@Injectable({
  providedIn: 'root',
})
export class ParkingsApiService {
  private _httpClient = inject(HttpClient);
  private _globalLoaderService = inject(GlobalLoaderService);

  private _parkingsList = signal<ParkingPoint[]>([]);

  parkingFilter = signal<ParkingsFilter>(ParkingsFilter.ALL);

  private _filteredParkingsList = computed(() => {
    const list = this._parkingsList();
    const filter = this.parkingFilter();

    if (filter === ParkingsFilter.WELL_RATED) {
      return list.filter((p) => p.likeCount > p.dislikeCount);
    }

    return list;
  });

  getParkings(force = false): Signal<ParkingPoint[]> {
    if (force || this._parkingsList().length === 0) {
      this._fetchParkings();
    }

    return this._filteredParkingsList;
  }

  private _fetchParkings(): void {
    this._globalLoaderService.show();
    const url = `${environment.apiUrl}/parking-points/`;

    this._httpClient
      .get<ParkingPoint[]>(url)
      .pipe(finalize(() => this._globalLoaderService.hide()))
      .subscribe((res) => {
        res.forEach((parking: any) => {
          delete parking.createdAt;
          delete parking.updatedAt;
          delete parking.user;
        });
        const parkings = res.map((parking) => new ParkingPoint(parking));
        this._parkingsList.set(parkings);
      });
  }

  getParking(parkingPointId: number): Observable<ParkingPoint> {
    this._globalLoaderService.show();
    return this._httpClient
      .get<ParkingPoint>(`${environment.apiUrl}/parking-points/${parkingPointId}/`)
      .pipe(
        tap((res) => {
          const parking = new ParkingPoint(res);
          const parkingIndex = this._parkingsList().findIndex((item) => item.id === parking.id);
          if (parkingIndex !== -1) {
            this._parkingsList.update((parkings: ParkingPoint[]) =>
              parkings.toSpliced(parkingIndex, 1, parking),
            );
          }
        }),
        finalize(() => this._globalLoaderService.hide()),
      );
  }

  postParking(body: ParkingPointSaveData): Observable<ParkingPoint> {
    this._globalLoaderService.show();
    return this._httpClient.post<ParkingPoint>(`${environment.apiUrl}/parking-points/`, body).pipe(
      tap((newParking: ParkingPoint) =>
        this._parkingsList.update((parkings: ParkingPoint[]) => [
          ...parkings,
          new ParkingPoint(newParking),
        ]),
      ),
      finalize(() => this._globalLoaderService.hide()),
    );
  }
}
